import { useEffect, useRef, useCallback } from 'react';

/**
 * Xbox Controller Button Mapping (Standard Gamepad)
 * 
 * buttons[0] = A (South) → Play/Pause
 * buttons[1] = B (East) → Next Track
 * buttons[2] = X (West) → Previous Track
 * buttons[3] = Y (North) → Voice Command
 * buttons[4] = LB (Left Bumper)
 * buttons[5] = RB (Right Bumper)
 * buttons[6] = LT (Left Trigger) → Volume Down (CONTINUOUS)
 * buttons[7] = RT (Right Trigger) → Volume Up (CONTINUOUS)
 * buttons[8] = Back/View → Toggle Media Player
 * buttons[9] = Start/Menu → Shuffle
 * buttons[10] = Left Stick Press
 * buttons[11] = Right Stick Press
 * buttons[12] = D-pad Up
 * buttons[13] = D-pad Down
 * buttons[14] = D-pad Left
 * buttons[15] = D-pad Right
 */

function GamepadController({
  onPlayPause,
  onNext,
  onPrevious,
  onVoiceCommand,
  onVolumeUp,
  onVolumeDown,
  onTogglePlayer,
  onShuffle,
  isPlaying
}) {
  const animationFrameRef = useRef(null);
  const lastButtonStates = useRef({});
  const connectedGamepad = useRef(null);
  const volumeThrottleRef = useRef(0);
  const comboTriggeredRef = useRef(false); // For LB+RB combo

  // Handle button press (only trigger on initial press, not hold)
  const handleButtonPress = useCallback((buttonIndex) => {
    switch (buttonIndex) {
      case 0: // A - Select (nav, album, or toolbar)
        console.log('🎮 Xbox: A pressed → Select');
        window.dispatchEvent(new CustomEvent('gamepadSelect'));
        window.dispatchEvent(new CustomEvent('gamepadAlbumSelect'));
        window.dispatchEvent(new CustomEvent('gamepadToolbarSelect'));
        break;
      case 1: // B - Back / Next Track
        console.log('🎮 Xbox: B pressed → Back/Next');
        window.dispatchEvent(new CustomEvent('gamepadBack'));
        if (onNext) onNext();
        break;
      case 2: // X - Previous
        console.log('🎮 Xbox: X pressed → Previous');
        if (onPrevious) onPrevious();
        break;
      case 3: // Y - Voice Command
        console.log('🎮 Xbox: Y pressed → Voice Command');
        if (onVoiceCommand) onVoiceCommand();
        break;
      case 8: // Back/View - Toggle Player
        console.log('🎮 Xbox: Back pressed → Toggle Player');
        if (onTogglePlayer) onTogglePlayer();
        break;
      case 9: // Start/Menu - Play/Pause
        console.log('🎮 Xbox: Start pressed → Play/Pause');
        if (onPlayPause) onPlayPause();
        break;
      case 4: // LB - (available for future use)
        console.log('🎮 Xbox: LB pressed');
        break;
      case 5: // RB - (available for future use)
        console.log('🎮 Xbox: RB pressed');
        break;
      case 10: // L3 (Left Stick Press) - Select in LeftNav
        console.log('🎮 Xbox: L3 pressed → Select');
        window.dispatchEvent(new CustomEvent('gamepadSelect'));
        break;
      case 11: // R3 (Right Stick Press) - Select album in grid OR toolbar
        console.log('🎮 Xbox: R3 pressed → Select Album/Toolbar');
        window.dispatchEvent(new CustomEvent('gamepadAlbumSelect'));
        window.dispatchEvent(new CustomEvent('gamepadToolbarSelect'));
        break;
      default:
        break;
    }
  }, [onPlayPause, onNext, onPrevious, onVoiceCommand, onTogglePlayer, onShuffle]);

  // Handle continuous trigger input (LT/RT for volume)
  const handleTriggers = useCallback((gamepad) => {
    const now = Date.now();
    // Throttle volume changes to every 100ms for smooth but controlled adjustment
    if (now - volumeThrottleRef.current < 100) return;
    
    const ltValue = gamepad.buttons[6].value; // Left Trigger (0.0 to 1.0)
    const rtValue = gamepad.buttons[7].value; // Right Trigger (0.0 to 1.0)
    
    // Volume down - Left Trigger held
    if (ltValue > 0.1) {
      const delta = -0.05 * ltValue; // Harder press = faster change
      if (onVolumeDown) onVolumeDown(delta);
      volumeThrottleRef.current = now;
    }
    
    // Volume up - Right Trigger held
    if (rtValue > 0.1) {
      const delta = 0.05 * rtValue; // Harder press = faster change
      if (onVolumeUp) onVolumeUp(delta);
      volumeThrottleRef.current = now;
    }
  }, [onVolumeUp, onVolumeDown]);

  // Handle joystick navigation
  const joystickThrottleRef = useRef(0);
  const lastJoystickDirection = useRef(null);
  const rightJoystickThrottleRef = useRef(0);
  const lastRightJoystickDirection = useRef(null);
  
  const handleJoystickNavigation = useCallback((gamepad) => {
    const now = Date.now();
    const threshold = 0.6; // Joystick must be pushed past this threshold
    const repeatDelay = 200; // ms between repeated navigation
    
    // LEFT JOYSTICK - LeftAccordion navigation
    const leftX = gamepad.axes[0];
    const leftY = gamepad.axes[1];
    
    let leftDirection = null;
    
    if (Math.abs(leftY) > threshold && Math.abs(leftY) > Math.abs(leftX)) {
      leftDirection = leftY > 0 ? 'down' : 'up';
    } else if (Math.abs(leftX) > threshold) {
      leftDirection = leftX > 0 ? 'right' : 'left';
    }
    
    if (leftDirection && (leftDirection !== lastJoystickDirection.current || now - joystickThrottleRef.current > repeatDelay)) {
      console.log('🎮 Left Joystick:', leftDirection);
      window.dispatchEvent(new CustomEvent('gamepadNavigate', { detail: { direction: leftDirection } }));
      joystickThrottleRef.current = now;
      lastJoystickDirection.current = leftDirection;
    }
    
    if (Math.abs(leftX) < 0.3 && Math.abs(leftY) < 0.3) {
      lastJoystickDirection.current = null;
    }
    
    // RIGHT JOYSTICK - AlbumGrid navigation
    const rightX = gamepad.axes[2];
    const rightY = gamepad.axes[3];
    
    let rightDirection = null;
    
    if (Math.abs(rightY) > threshold && Math.abs(rightY) > Math.abs(rightX)) {
      rightDirection = rightY > 0 ? 'down' : 'up';
    } else if (Math.abs(rightX) > threshold) {
      rightDirection = rightX > 0 ? 'right' : 'left';
    }
    
    if (rightDirection && (rightDirection !== lastRightJoystickDirection.current || now - rightJoystickThrottleRef.current > repeatDelay)) {
      console.log('🎮 Right Joystick:', rightDirection);
      window.dispatchEvent(new CustomEvent('gamepadAlbumNavigate', { detail: { direction: rightDirection } }));
      rightJoystickThrottleRef.current = now;
      lastRightJoystickDirection.current = rightDirection;
    }
    
    if (Math.abs(rightX) < 0.3 && Math.abs(rightY) < 0.3) {
      lastRightJoystickDirection.current = null;
    }
  }, []);

  // Poll gamepad state
  const pollGamepad = useCallback(() => {
    const gamepads = navigator.getGamepads();
    const gamepad = gamepads[connectedGamepad.current];

    if (gamepad) {
      // Check each button for press events
      gamepad.buttons.forEach((button, index) => {
        const isPressed = button.pressed;
        const wasPressed = lastButtonStates.current[index] || false;

        // Trigger on press (not release) - skip triggers (6,7) handled separately
        if (isPressed && !wasPressed && index !== 6 && index !== 7) {
          handleButtonPress(index);
        }

        lastButtonStates.current[index] = isPressed;
      });
      
      // Handle triggers continuously
      handleTriggers(gamepad);
      
      // Handle joystick navigation
      handleJoystickNavigation(gamepad);
      
      // Check for LB + RB combo (refresh)
      const lbPressed = gamepad.buttons[4]?.pressed;
      const rbPressed = gamepad.buttons[5]?.pressed;
      
      if (lbPressed && rbPressed && !comboTriggeredRef.current) {
        console.log('🎮 Xbox: LB + RB pressed → Refresh Page');
        comboTriggeredRef.current = true;
        window.location.reload();
      }
      
      // Reset combo when both released
      if (!lbPressed && !rbPressed) {
        comboTriggeredRef.current = false;
      }
    }

    animationFrameRef.current = requestAnimationFrame(pollGamepad);
  }, [handleButtonPress, handleTriggers, handleJoystickNavigation]);

  useEffect(() => {
    // Gamepad connected
    const handleGamepadConnected = (e) => {
      console.log('🎮 Gamepad connected:', e.gamepad.id);
      console.log('🎮 Buttons available:', e.gamepad.buttons.length);
      console.log('🎮 Axes available:', e.gamepad.axes.length);
      console.log('🎮 Mapping:', e.gamepad.mapping);
      connectedGamepad.current = e.gamepad.index;
      lastButtonStates.current = {};
      
      // Debug: dump state every 2 seconds
      const debugInterval = setInterval(() => {
        const gamepads = navigator.getGamepads();
        const gp = gamepads[connectedGamepad.current];
        if (gp) {
          console.log('🎮 === GAMEPAD STATE ===');
          gp.buttons.forEach((btn, i) => {
            if (btn.pressed || btn.value > 0.1) {
              console.log(`  Button ${i}: pressed=${btn.pressed}, value=${btn.value.toFixed(2)}`);
            }
          });
          gp.axes.forEach((axis, i) => {
            if (Math.abs(axis) > 0.1) {
              console.log(`  Axis ${i}: ${axis.toFixed(2)}`);
            }
          });
        }
      }, 2000);
      
      // Store interval for cleanup
      connectedGamepad.debugInterval = debugInterval;
      
      // Show notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('🎮 Controller Connected', {
          body: `${e.gamepad.id}\nA=Play, B=Next, X=Prev, Y=Voice`,
          icon: '🎮'
        });
      }
      
      // Start polling
      animationFrameRef.current = requestAnimationFrame(pollGamepad);
    };

    // Gamepad disconnected
    const handleGamepadDisconnected = (e) => {
      console.log('🎮 Gamepad disconnected:', e.gamepad.id);
      if (connectedGamepad.current === e.gamepad.index) {
        connectedGamepad.current = null;
        cancelAnimationFrame(animationFrameRef.current);
      }
    };

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);

    // Check if gamepad is already connected
    const gamepads = navigator.getGamepads();
    for (let i = 0; i < gamepads.length; i++) {
      if (gamepads[i]) {
        console.log('🎮 Found existing gamepad:', gamepads[i].id);
        connectedGamepad.current = i;
        animationFrameRef.current = requestAnimationFrame(pollGamepad);
        break;
      }
    }

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [pollGamepad]);

  // This component doesn't render anything
  return null;
}

export default GamepadController;

